import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Clock, TrendingUp, GraduationCap } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  age?: number;
  level?: string;
  nextClass?: {
    name: string;
    time: string;
    date: string;
  };
  attendance?: number;
  status?: 'active' | 'inactive' | 'on-break';
  profilePicture?: string;
  colorScheme?: {
    primary: string;
    light: string;
    lighter: string;
  };
}

interface CanvaStudentCardProps {
  student: Student;
  onClick?: (student: Student) => void;
  className?: string;
}

const getStudentColorScheme = (studentId: string) => {
  const colors = [
    { primary: '#3b82f6', light: '#dbeafe', lighter: '#eff6ff' }, // Blue
    { primary: '#10b981', light: '#d1fae5', lighter: '#ecfdf5' }, // Green
    { primary: '#8b5cf6', light: '#e9d5ff', lighter: '#f3e8ff' }, // Purple
    { primary: '#f59e0b', light: '#fed7aa', lighter: '#fff7ed' }, // Orange
    { primary: '#ef4444', light: '#fecaca', lighter: '#fef2f2' }, // Red
    { primary: '#06b6d4', light: '#cffafe', lighter: '#ecfeff' }, // Cyan
  ];
  
  // Simple hash to consistently assign colors based on student ID
  const hash = studentId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

const getInitials = (firstName: string, lastName: string) => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

const formatAttendance = (attendance?: number) => {
  if (attendance === undefined) return 0;
  return Math.round(attendance);
};

export const CanvaStudentCard: React.FC<CanvaStudentCardProps> = ({
  student,
  onClick,
  className
}) => {
  const colorScheme = student.colorScheme || getStudentColorScheme(student.id);
  const initials = getInitials(student.firstName, student.lastName);
  const attendancePercentage = formatAttendance(student.attendance);
  
  return (
    <motion.div
      className={cn("canva-student-card canva-card-hover", className)}
      onClick={() => onClick?.(student)}
      style={{
        '--student-color-light': colorScheme.light,
        '--student-color-lighter': colorScheme.lighter,
      } as React.CSSProperties}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3 }}
    >
      {/* Decorative background element */}
      <div 
        className="absolute top-0 right-0 w-20 h-20 rounded-bl-3xl opacity-30"
        style={{ backgroundColor: colorScheme.light }}
      />
      
      {/* Student Info Header */}
      <div className="flex items-center gap-4 mb-6 relative z-10">
        <div 
          className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
          style={{ 
            background: `linear-gradient(135deg, ${colorScheme.primary} 0%, ${colorScheme.primary}dd 100%)` 
          }}
        >
          {student.profilePicture ? (
            <img 
              src={student.profilePicture} 
              alt={`${student.firstName} ${student.lastName}`}
              className="w-full h-full object-cover rounded-2xl"
            />
          ) : (
            <span className="text-white font-bold text-lg">{initials}</span>
          )}
        </div>
        
        <div className="flex-1">
          <h3 className="font-bold text-gray-900 text-lg">
            {student.firstName} {student.lastName}
          </h3>
          <p className="text-gray-600 font-medium">
            {student.age && `Age ${student.age}`}
            {student.age && student.level && ' • '}
            {student.level && `Level ${student.level}`}
          </p>
          <div className="flex items-center gap-1 mt-1">
            <span 
              className="w-2 h-2 rounded-full canva-pulse-dot"
              style={{ backgroundColor: student.status === 'active' ? '#10b981' : '#f59e0b' }}
            />
            <span 
              className="text-xs font-medium capitalize"
              style={{ color: student.status === 'active' ? '#10b981' : '#f59e0b' }}
            >
              {student.status || 'active'}
            </span>
          </div>
        </div>
      </div>

      {/* Student Stats */}
      <div className="space-y-4 mb-6">
        {/* Next Class */}
        {student.nextClass && (
          <div 
            className="rounded-xl p-4"
            style={{ backgroundColor: colorScheme.lighter }}
          >
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600">Next Class:</span>
              <span className="text-sm font-bold text-gray-900">
                {student.nextClass.time}
              </span>
            </div>
            <div className="text-sm text-gray-700 font-medium">
              {student.nextClass.name}
            </div>
          </div>
        )}

        {/* Attendance */}
        <div 
          className="rounded-xl p-4"
          style={{ backgroundColor: colorScheme.lighter }}
        >
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600">Attendance:</span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-green-600">
                {attendancePercentage}%
              </span>
            </div>
          </div>
          <div className="canva-progress-bar">
            <div 
              className="canva-progress-fill" 
              style={{ width: `${attendancePercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Action Button */}
      <motion.button
        className="w-full py-3 px-4 rounded-xl font-semibold text-white shadow-lg transition-all"
        style={{ 
          background: `linear-gradient(135deg, ${colorScheme.primary} 0%, ${colorScheme.primary}cc 100%)` 
        }}
        whileHover={{ 
          scale: 1.02,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}
        whileTap={{ scale: 0.98 }}
      >
        View Details
        <ChevronRight className="inline-block w-4 h-4 ml-2" />
      </motion.button>
    </motion.div>
  );
};

export default CanvaStudentCard;